import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stack,
    TextField,
    MenuItem,
    Checkbox,
    ListItemText,
    Typography,
    Alert,
} from "@mui/material";
import { useQuery, useMutation } from "@apollo/client";

import { GET_OPEN_BEDS } from "../../../../platform/graphql/queries/care";
import { RESERVE_REQUEST_BEDS } from "../../../../platform/graphql/mutations/care";

export type Plan = "TWO";

interface PricingFlags {
    feePerBed: number;
}

interface OpenBedOption {
    id: string;
    label: string;
}

interface BedMultiSettleDialogProps {
    open: boolean;
    businessId: number;
    lead: any;
    pricingFlags?: PricingFlags;
    onClose: () => void;
    onReserved: (p: {
        bedIds: string[];
        feePerBed: number;
        total: number;
        amountDueNow: number;
        plan: Plan;
    }) => void;
}

function computeInstallments(total: number): number[] {
    if (!Number.isFinite(total) || total <= 0) return [];

    const cents = Math.round(total * 100);
    const firstPayment = Math.round(cents / 2);
    const secondPayment = cents - firstPayment;

    return [firstPayment / 100, secondPayment / 100];
}

function fmt(n: number) {
    return n.toLocaleString(undefined, {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
    });
}

function normalizePayerType(value: any): "PRIVATE" | "MEDICAID" | "MEDICARE" | "VA" | "UNKNOWN" {
    const up = String(value || "").trim().toUpperCase();
    if (up === "PRIVATE_PAY") return "PRIVATE";
    if (["PRIVATE", "MEDICAID", "MEDICARE", "VA", "UNKNOWN"].includes(up as any)) {
        return up as any;
    }
    return "UNKNOWN";
}

export const BedMultiSettleDialog: React.FC<
    BedMultiSettleDialogProps
> = ({
    open,
    businessId,
    lead,
    pricingFlags,
    onClose,
    onReserved,
}) => {
    const { data } = useQuery(GET_OPEN_BEDS, {
        variables: { businessId },
        skip: !open || !businessId,
        fetchPolicy: "cache-and-network",
    });

    const openBeds = React.useMemo<OpenBedOption[]>(() => {
        const rooms = data?.getRoomsByBusiness ?? [];
        return rooms.flatMap((r: any) =>
            (r.beds ?? [])
                .filter((b: any) => !b.isOccupied)
                .map((b: any) => ({
                    id: String(b.id),
                    label: `Room ${r.roomNumber ?? r.id} - Bed ${b.bedLabel ?? b.id}`,
                }))
        );
    }, [data]);

    const [selectedBedIds, setSelectedBedIds] = React.useState<string[]>([]);
    const bedsCount = selectedBedIds.length;
    const normalizedPayerType = React.useMemo(
        () => normalizePayerType(lead?.payerType),
        [lead?.payerType]
    );
    const isFeeFreeReferral = normalizedPayerType !== "PRIVATE";

    const [totalInput, setTotalInput] = React.useState("");
    const total = isFeeFreeReferral ? 0 : Number(totalInput);

    const [error, setError] = React.useState<string | null>(null);
    const feePerBed = isFeeFreeReferral ? 0 : (pricingFlags?.feePerBed ?? 0);
    const plan: Plan = "TWO";

    const installments = computeInstallments(total);
    const amountDueNow = installments[0] ?? 0;

    React.useEffect(() => {
        if (!bedsCount) {
            setError("Select at least one bed.");
        } else if (!isFeeFreeReferral && (!Number.isFinite(total) || total <= 0)) {
            setError("Enter a valid total amount.");
        } else {
            setError(null);
        }
    }, [bedsCount, isFeeFreeReferral, total]);

    const [reserveBeds, { loading }] = useMutation(
        RESERVE_REQUEST_BEDS
    );

    const handleContinue = async () => {
        if (error || !lead?.id) return;
        try {
            await reserveBeds({
                variables: {
                    id: String(lead.id),
                    bedIds: selectedBedIds,
                    feePerBed,
                    total,
                    amountDueNow,
                    plan,
                },
            });

            onReserved({
                bedIds: selectedBedIds,
                feePerBed,
                total,
                amountDueNow,
                plan,
            });

            onClose();
        } catch (e: any) {
            setError(e?.message || "Failed to prepare settlement payment.");
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                Mark Settled - {lead?.referrer?.contactName ?? "Referral"}
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2}>
                    <TextField
                        select
                        label="Assign Beds"
                        fullWidth
                        SelectProps={{
                            multiple: true,
                            value: selectedBedIds,
                            onChange: (e) =>
                                setSelectedBedIds(e.target.value as string[]),
                            renderValue: (selected) =>
                                (selected as string[]).length
                                    ? `${(selected as string[]).length} beds selected`
                                    : "None",
                        }}
                    >
                        {openBeds.map((b) => (
                            <MenuItem key={b.id} value={b.id}>
                                <Checkbox
                                    checked={selectedBedIds.includes(b.id)}
                                />
                                <ListItemText primary={b.label} />
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        label="Total placement fee (USD)"
                        type="number"
                        value={totalInput}
                        onChange={(e) => setTotalInput(e.target.value)}
                        fullWidth
                        sx={{ display: isFeeFreeReferral ? "none" : undefined }}
                    />

                    {isFeeFreeReferral && (
                        <Alert severity="info">
                            No placement fee is required for {normalizedPayerType === "UNKNOWN" ? "this" : normalizedPayerType.toLowerCase()} referral.
                            Assign beds and continue to mark it settled.
                        </Alert>
                    )}

                    {installments.length > 0 && !isFeeFreeReferral && (
                        <Alert severity="success">
                            <Typography fontWeight={600}>
                                Payment schedule
                            </Typography>
                            <Typography>
                                Due now - <strong>{fmt(installments[0] ?? 0)}</strong>
                            </Typography>
                            <Typography>
                                Second payment - <strong>{fmt(installments[1] ?? 0)}</strong>
                            </Typography>
                            <Typography variant="caption">
                                Settlement is split into two payments only: 50% now and the remaining balance as the second payment.
                            </Typography>
                        </Alert>
                    )}

                    {error && <Alert severity="error">{error}</Alert>}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleContinue}
                    disabled={!!error || loading}
                >
                    {isFeeFreeReferral ? "Confirm Settlement" : `Pay ${fmt(amountDueNow)} now`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
